import type { ServerQuery } from './paging.ts';

/**
 * /question/count depends on the filter only, so it is asked once per filter, not once per page.
 *
 * A filtered count scans the whole bank (measured 1.3-7.8 s on prod against 0.3-0.5 s for the page),
 * so a page turn must not repeat it, and the page must never wait for it.
 */
export interface CountRequest {
  /** The filter this count belongs to. */
  key: string;
  /** True when this call sent the request; false when it reused the one already out or landed. */
  fresh: boolean;
  /** The count, or null when it failed - a pager without a total still pages. */
  request: Promise<number | null>;
}

export interface CountCache {
  countFor: (filter: ServerQuery) => CountRequest;
  /** Whether `key` is still the filter being counted - a late answer for an old filter must not land. */
  isCurrent: (key: string) => boolean;
  /** Rows were added or removed, so the next countFor asks again. */
  forget: () => void;
}

export function createCountCache(count: (filter: ServerQuery) => Promise<number>): CountCache {
  let currentKey: string | null = null;
  let current: Promise<number | null> = Promise.resolve(null);
  return {
    countFor(filter) {
      const key = JSON.stringify(filter);
      const fresh = key !== currentKey;
      if (fresh) {
        currentKey = key;
        current = count(filter).then((total) => total, () => null);
      }
      return { key, fresh, request: current };
    },
    isCurrent: (key) => key === currentKey,
    forget() {
      currentKey = null;
    },
  };
}
